import { SOCKET_EVENTS } from '@/api/socket-events';
import ClientPhotoViewer from '@/components/client/client-photo-viewer';
import {
	$appendClientDetailPhotos,
	$clientDetailPhotos,
	$setClientDetailPhotos,
	type IClientPhoto,
} from '@/entities/client.entity';
import { useClientVirtualFeed } from '@/hooks/use-client-virtual-feed';
import { photoFileUrl } from '@/lib/client-photo-url';
import { getPhotoMatchCategoryLabel, isPhotoMatchCategoryId } from '@crave/config/photo-match-categories';
import { useUnit } from 'effector-react';
import { useState } from 'react';

const PHOTO_GRID_COLUMNS = 2;

interface ClientPhotoGridProps {
	clientId: string;
	searchText: string;
	enabled: boolean;
}

function categoryLabel(category: string): string {
	return isPhotoMatchCategoryId(category) ? getPhotoMatchCategoryLabel(category) : category;
}

function formatTakenAt(value: string | null): string | null {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

function PhotoCard({
	clientId,
	photo,
	onOpen,
}: {
	clientId: string;
	photo: IClientPhoto;
	onOpen: () => void;
}) {
	const taken = formatTakenAt(photo.takenAt);

	return (
		<button
			type='button'
			onClick={onOpen}
			className='w-full rounded-lg border border-border/60 overflow-hidden bg-card text-left transition-colors hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
		>
			<div className='aspect-square bg-muted'>
				<img
					src={photoFileUrl(clientId, photo.id, 'thumb')}
					alt={categoryLabel(photo.category)}
					className='h-full w-full object-cover'
					loading='lazy'
					draggable={false}
				/>
			</div>
			<div className='p-2 text-xs space-y-0.5'>
				<div className='font-medium truncate'>{categoryLabel(photo.category)}</div>
				{taken && <div className='text-muted-foreground truncate'>{taken}</div>}
			</div>
		</button>
	);
}

export default function ClientPhotoGrid({ clientId, searchText, enabled }: ClientPhotoGridProps) {
	const [photos, setPhotos, appendPhotos] = useUnit([
		$clientDetailPhotos,
		$setClientDetailPhotos,
		$appendClientDetailPhotos,
	]);
	const [viewerOpen, setViewerOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);

	function openViewer(index: number) {
		setActiveIndex(index);
		setViewerOpen(true);
	}

	const { listRef, content } = useClientVirtualFeed<IClientPhoto>({
		clientId,
		searchText,
		enabled,
		fetchEvent: SOCKET_EVENTS.getClientPhotos,
		responseEvent: SOCKET_EVENTS.addClientPhotos,
		items: photos,
		setItems: setPhotos,
		appendItems: appendPhotos,
		getItemId: item => item.id,
		columns: PHOTO_GRID_COLUMNS,
		estimateSize: 200,
		emptyLabel: 'Совпадающих фото нет',
		renderItem: () => null,
		renderRow: (rowItems, rowIndex) => (
			<div className='grid grid-cols-2 gap-2'>
				{rowItems.map((photo, columnIndex) => {
					const itemIndex = rowIndex * PHOTO_GRID_COLUMNS + columnIndex;
					return (
						<PhotoCard
							key={photo.id}
							clientId={clientId}
							photo={photo}
							onOpen={() => openViewer(itemIndex)}
						/>
					);
				})}
			</div>
		),
	});

	return (
		<>
			<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
				{content}
			</div>
			<ClientPhotoViewer
				open={viewerOpen}
				onOpenChange={setViewerOpen}
				clientId={clientId}
				photos={photos}
				activeIndex={activeIndex}
				onActiveIndexChange={setActiveIndex}
			/>
		</>
	);
}
